/* eslint-disable react-refresh/only-export-components -- consolidated desktop context intentionally exports the provider, hooks, and types together */
import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';

export type AppId = 'feed' | 'dms' | 'relays' | 'media' | 'nsite' | 'profile' | 'settings' | 'notifications' | 'articles' | 'publish' | 'mail' | 'documents' | 'search';

const APP_IDS: readonly AppId[] = ['feed', 'dms', 'relays', 'media', 'nsite', 'profile', 'settings', 'notifications', 'articles', 'publish', 'mail', 'documents', 'search'];

export interface WindowState {
  id: string;
  appId: AppId;
  title: string;
  x: number;
  y: number;
  width: number;
  height: number;
  isMinimized: boolean;
  isMaximized: boolean;
  zIndex: number;
  /** Optional per-window data payload (e.g. pubkey for profile windows) */
  data?: Record<string, unknown>;
}

interface DesktopContextType {
  windows: WindowState[];
  activeWindowId: string | null;
  openWindow: (appId: AppId, title: string) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  maximizeWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  /** Commit any combination of position/size in a single state update (used by drag-end and snapping) */
  updateWindowGeometry: (id: string, geometry: Partial<Pick<WindowState, 'x' | 'y' | 'width' | 'height' | 'isMaximized'>>) => void;
  /** Reset a window's position and size to its defaults and bring it on-screen. */
  recenterWindow: (appId: AppId) => void;
  wallpaper: string | null;
  setWallpaper: (url: string | null) => void;
  openThread: (event: NostrEvent) => void;
  pendingThread: NostrEvent | null;
  clearPendingThread: () => void;
  /** Open the nsite browser and navigate directly to a URL */
  openNsite: (url: string) => void;
  pendingNsiteUrl: string | null;
  clearPendingNsiteUrl: () => void;
  /** Open the Profile app focused on a specific pubkey (hex) */
  openProfile: (pubkey: string) => void;
  /** Open the Feed thread view for a known event ID (fetches the event if needed) */
  openEventById: (id: string, authorHint?: string) => void;
}

export const DesktopContext = createContext<DesktopContextType | undefined>(undefined);

/** Height reserved for the dock at the bottom of the screen (must match AppWindow.tsx). */
const DOCK_HEIGHT = 64;

let nextZIndex = 100;
let windowCounter = 0;

// ─── Session persistence ──────────────────────────────────────────────────────
// The desktop remembers its layout across reloads: open windows (with geometry
// and per-window data) and the wallpaper choice.

const SESSION_KEY = 'nostrvm:desktop-session:v1';

interface DesktopSession {
  windows: WindowState[];
  wallpaper: string | null;
}

function loadSession(): DesktopSession {
  const empty: DesktopSession = { windows: [], wallpaper: null };
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<DesktopSession>;
    const maxW = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const maxH = typeof window !== 'undefined' ? window.innerHeight : 1080;
    const windows = (Array.isArray(parsed.windows) ? parsed.windows : [])
      .filter((w): w is WindowState =>
        !!w && typeof w === 'object' &&
        typeof w.id === 'string' &&
        APP_IDS.includes(w.appId as AppId) &&
        typeof w.x === 'number' && typeof w.y === 'number' &&
        typeof w.width === 'number' && typeof w.height === 'number'
      )
      .map((w) => ({
        ...w,
        // Keep restored windows reachable on the current viewport
        x: Math.min(Math.max(0, w.x), Math.max(0, maxW - 160)),
        y: Math.min(Math.max(0, w.y), Math.max(0, maxH - 120)),
        width: Math.min(w.width, maxW),
        height: Math.min(w.height, maxH),
      }));
    // Re-seed module counters so new windows never collide with restored ones
    for (const w of windows) {
      nextZIndex = Math.max(nextZIndex, w.zIndex);
      const num = Number(w.id.split('-').pop());
      if (Number.isFinite(num)) windowCounter = Math.max(windowCounter, num);
    }
    return {
      windows,
      wallpaper: typeof parsed.wallpaper === 'string' ? parsed.wallpaper : null,
    };
  } catch (error) {
    console.warn('Failed to restore desktop session:', error);
    return empty;
  }
}

function saveSession(session: DesktopSession) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Quota errors etc. — losing layout persistence is non-fatal
  }
}

function getDefaultWindowPosition(appId: AppId, existingWindows: WindowState[]): { x: number; y: number } {
  const baseX = 100 + (existingWindows.length * 30) % 200;
  const baseY = 50 + (existingWindows.length * 30) % 150;
  return { x: baseX, y: baseY };
}

function getDefaultWindowSize(appId: AppId): { width: number; height: number } {
  switch (appId) {
    case 'feed':
      return { width: 520, height: 680 };
    case 'dms':
      return { width: 480, height: 600 };
    case 'relays':
      return { width: 500, height: 500 };
    case 'media':
      return { width: 700, height: 550 };
    case 'nsite':
      return { width: 900, height: 650 };
    case 'profile':
      return { width: 480, height: 600 };
    case 'settings':
      return { width: 420, height: 500 };
    case 'notifications':
      return { width: 460, height: 620 };
    case 'articles':
      return { width: 620, height: 800 };
    case 'publish':
      return { width: 500, height: 340 };
    case 'mail':
      return { width: 900, height: 600 };
    case 'documents':
      return { width: 1000, height: 680 };
    case 'search':
      return { width: 520, height: 680 };
    default:
      return { width: 600, height: 500 };
  }
}

export function DesktopProvider({ children }: { children: ReactNode }) {
  const [session] = useState(loadSession);
  const [windows, setWindows] = useState<WindowState[]>(session.windows);
  const [activeWindowId, setActiveWindowId] = useState<string | null>(null);
  const [wallpaper, setWallpaper] = useState<string | null>(session.wallpaper);
  const [pendingThread, setPendingThread] = useState<NostrEvent | null>(null);
  const [pendingNsiteUrl, setPendingNsiteUrl] = useState<string | null>(null);

  useEffect(() => {
    saveSession({ windows, wallpaper });
  }, [windows, wallpaper]);

  const openWindow = useCallback((appId: AppId, title: string) => {
    setWindows((prev) => {
      // Find any existing window for this app (visible OR minimized).
      // Profile windows are intentionally excluded — multiple profiles can
      // coexist, so the lookup is skipped for them.
      const existing = appId !== 'profile' ? prev.find((w) => w.appId === appId) : undefined;
      if (existing) {
        // Restore / focus the existing window
        setActiveWindowId(existing.id);
        nextZIndex += 1;
        return prev.map((w) =>
          w.id === existing.id ? { ...w, zIndex: nextZIndex, isMinimized: false } : w
        );
      }

      const id = `${appId}-${++windowCounter}`;
      const pos = getDefaultWindowPosition(appId, prev);
      const size = getDefaultWindowSize(appId);
      nextZIndex += 1;
      setActiveWindowId(id);

      return [
        ...prev,
        {
          id,
          appId,
          title,
          x: pos.x,
          y: pos.y,
          width: size.width,
          height: size.height,
          isMinimized: false,
          isMaximized: false,
          zIndex: nextZIndex,
        },
      ];
    });
  }, []);

  const openThread = useCallback((event: NostrEvent) => {
    // Ensure feed window is open and focused
    openWindow('feed', 'Nostr Feed');
    // Set pending thread so FeedApp can pick it up
    setPendingThread(event);
  }, [openWindow]);

  const openNsite = useCallback((url: string) => {
    openWindow('nsite', 'Nsite Browser');
    setPendingNsiteUrl(url);
  }, [openWindow]);

  const clearPendingNsiteUrl = useCallback(() => {
    setPendingNsiteUrl(null);
  }, []);

  const openProfile = useCallback((pubkey: string) => {
    // Always open a fresh profile window so multiple profiles can be viewed side-by-side
    setWindows((prev) => {
      const id = `profile-${++windowCounter}`;
      const size = getDefaultWindowSize('profile');
      nextZIndex += 1;
      const offsetX = 100 + (prev.length * 30) % 200;
      const offsetY = 50 + (prev.length * 30) % 150;
      setActiveWindowId(id);
      return [
        ...prev,
        {
          id,
          appId: 'profile',
          title: 'Profile',
          x: offsetX,
          y: offsetY,
          width: size.width,
          height: size.height,
          isMinimized: false,
          isMaximized: false,
          zIndex: nextZIndex,
          data: { pubkey },
        },
      ];
    });
  }, []);

  const closeWindow = useCallback((id: string) => {
    setWindows((prev) => {
      const filtered = prev.filter((w) => w.id !== id);
      if (activeWindowId === id) {
        const topWindow = filtered
          .filter((w) => !w.isMinimized)
          .sort((a, b) => b.zIndex - a.zIndex)[0];
        setActiveWindowId(topWindow?.id ?? null);
      }
      return filtered;
    });
  }, [activeWindowId]);

  const minimizeWindow = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, isMinimized: true } : w))
    );
    setWindows((prev) => {
      const visible = prev.filter((w) => !w.isMinimized && w.id !== id);
      const top = visible.sort((a, b) => b.zIndex - a.zIndex)[0];
      setActiveWindowId(top?.id ?? null);
      return prev;
    });
  }, []);

  const maximizeWindow = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, isMaximized: true } : w))
    );
  }, []);

  const restoreWindow = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) =>
        w.id === id ? { ...w, isMinimized: false, isMaximized: false } : w
      )
    );
    setActiveWindowId(id);
    nextZIndex += 1;
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, zIndex: nextZIndex } : w))
    );
  }, []);

  const focusWindow = useCallback((id: string) => {
    setActiveWindowId(id);
    nextZIndex += 1;
    setWindows((prev) =>
      prev.map((w) =>
        w.id === id ? { ...w, zIndex: nextZIndex, isMinimized: false } : w
      )
    );
  }, []);

  const updateWindowGeometry = useCallback((id: string, geometry: Partial<Pick<WindowState, 'x' | 'y' | 'width' | 'height' | 'isMaximized'>>) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, ...geometry } : w))
    );
  }, []);

  const recenterWindow = useCallback((appId: AppId) => {
    setWindows((prev) => {
      const existing = prev.find((w) => w.appId === appId);
      if (!existing) return prev;
      const size = getDefaultWindowSize(appId);
      // Centre the window on the current viewport, leaving room for the dock
      const x = Math.max(0, Math.floor((window.innerWidth - size.width) / 2));
      const y = Math.max(0, Math.floor((window.innerHeight - DOCK_HEIGHT - size.height) / 2));
      nextZIndex += 1;
      setActiveWindowId(existing.id);
      return prev.map((w) =>
        w.id === existing.id
          ? { ...w, x, y, width: size.width, height: size.height, isMinimized: false, isMaximized: false, zIndex: nextZIndex }
          : w
      );
    });
  }, []);

  const openEventById = useCallback((id: string, authorHint?: string) => {
    // Build a minimal shell event — ThreadView fetches the real content by ID
    const shell: NostrEvent = {
      id,
      kind: 1,
      pubkey: authorHint ?? '',
      created_at: 0,
      content: '',
      tags: [],
      sig: '',
    };
    openThread(shell);
  }, [openThread]);

  const clearPendingThread = useCallback(() => {
    setPendingThread(null);
  }, []);

  // Memoized so context consumers (dock, palette, every window and app) only
  // re-render when something they can observe actually changes — not on every
  // provider render.
  const value = useMemo<DesktopContextType>(() => ({
    windows,
    activeWindowId,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    restoreWindow,
    focusWindow,
    updateWindowGeometry,
    recenterWindow,
    wallpaper,
    setWallpaper,
    openThread,
    pendingThread,
    clearPendingThread,
    openNsite,
    pendingNsiteUrl,
    clearPendingNsiteUrl,
    openProfile,
    openEventById,
  }), [
    windows,
    activeWindowId,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    restoreWindow,
    focusWindow,
    updateWindowGeometry,
    recenterWindow,
    wallpaper,
    openThread,
    pendingThread,
    clearPendingThread,
    openNsite,
    pendingNsiteUrl,
    clearPendingNsiteUrl,
    openProfile,
    openEventById,
  ]);

  return (
    <DesktopContext.Provider value={value}>
      {children}
    </DesktopContext.Provider>
  );
}

export function useDesktop() {
  const ctx = useContext(DesktopContext);
  if (!ctx) throw new Error('useDesktop must be used within DesktopProvider');
  return ctx;
}

// ─── Per-window data context ──────────────────────────────────────────────────
// Each AppWindow wraps its app component with this context so the component
// can read its own window-level data (e.g. the pubkey for a profile window).

const WindowDataContext = createContext<Record<string, unknown> | undefined>(undefined);

export function WindowDataProvider({
  data,
  children,
}: {
  data: Record<string, unknown> | undefined;
  children: ReactNode;
}) {
  return (
    <WindowDataContext.Provider value={data}>
      {children}
    </WindowDataContext.Provider>
  );
}

/** Returns the data payload stored on the current window (may be undefined). */
export function useWindowData(): Record<string, unknown> | undefined {
  return useContext(WindowDataContext);
}
